/**
 * Trip Review Service - Client-side integration for proactive trip review
 *
 * Calls the `tripReview` Cloud Function on page open.
 * Returns a proactive review message and any auto-created proposals.
 *
 * Cooldown management: remembers the last review time per session in
 * component state (passed in as lastReviewedAt). The Cloud Function also
 * enforces a server-side cooldown so duplicate calls are cheap.
 *
 * Usage:
 *   import { tripReviewService } from './tripReviewService';
 *   const result = await tripReviewService.requestReview({ effectiveUserId, tripId, sessionId, lastReviewedAt });
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

let _tripReviewCallable = null;

function getTripReviewCallable() {
  if (!_tripReviewCallable) {
    _tripReviewCallable = httpsCallable(getFunctions(), 'tripReview');
  }
  return _tripReviewCallable;
}

export const tripReviewService = {
  /**
   * Request a proactive trip review.
   *
   * @param {Object} params
   * @param {string} params.effectiveUserId - Trip owner user ID
   * @param {string} params.tripId - Trip ID
   * @param {string} [params.sessionId] - Current session ID (proposals associated to this session)
   * @param {string} [params.lastReviewedAt] - ISO string of last review time (for cooldown)
   * @returns {Promise<{reviewMessage, issues, proposals, skipped, skipReason, reviewedAt, model, error}>}
   */
  async requestReview({ effectiveUserId, tripId, sessionId, lastReviewedAt }) {
    if (!effectiveUserId || !tripId) {
      return {
        reviewMessage: '',
        issues: { metadataIssues: [], accommodationGaps: 0, emptyDays: 0, connectionIssues: 0, incompleteAccomm: 0, incompleteTravel: 0, completenessScore: 100 },
        proposals: [],
        skipped: true,
        skipReason: 'missing effectiveUserId or tripId'
      };
    }

    try {
      const callable = getTripReviewCallable();
      const result = await callable({
        ownerId: effectiveUserId,
        tripId,
        sessionId: sessionId || null,
        lastReviewedAt: lastReviewedAt || null
      });

      return result.data || {
        reviewMessage: '',
        issues: {},
        proposals: [],
        skipped: true,
        skipReason: 'empty response'
      };
    } catch (error) {
      console.warn('[tripReviewService] Review call failed:', error?.message);
      return {
        reviewMessage: '',
        issues: {},
        proposals: [],
        error: error?.message || 'Unknown error',
        skipped: true,
        skipReason: 'call failed'
      };
    }
  }
};
